import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';

import { fetchBase } from '@atlassian/pillar/runtime/fetch';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { appendHeader, cfetch, networkTracker, shouldUseGetMethod } from '@confluence/network';
import { WaterfallMeasures } from '@confluence/action-measures/entry-points/WaterfallMeasures.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { getAtlAttributionHeader } from '@confluence/query-preloader-tools/entry-points/getAtlAttributionHeader.ts';
import { getQueryName } from '@confluence/query-preloader-tools/entry-points/getQueryName.ts';
import { trackInflightRequest } from '@confluence/query-preloader-boundary/entry-points/inflightRequests.ts';
import { getQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';
import { getAsyncLocalStorage } from '@confluence/async-local-storage/entry-points/getAsyncLocalStorage.ts';
import { SLOW_QUERY_GUARD_STORAGE_NAME } from '@confluence/async-local-storage/entry-points/storageNames.ts';

import {
	createExperienceAwareFetch,
	isExperienceAwareFetchAlwaysEnabled,
} from './createExperienceAwareFetch.ts';
import { isGCPtenant } from './utils/gcpDomainUtils.ts';
import { getBrowserTabId } from './getBrowserTabId.ts';

const experienceAwareFetch = createExperienceAwareFetch();

export const AGG_SLOW_URI = '/gateway/api/graphql/slow';
const SITE_INFORMATION_QUERY_OPERATION_NAME = 'SiteInformationQuery';

const CC_GRAPHQL_SERVICE_NAME = 'cc-graphql';
const CC_GRAPHQL_ARCHETYPE_NAME = 'cc-graphql-archetype';

// appends the operation names as query params, and
// includes them as headers as well
interface CreateWrappedFetchOptions {
	isForAggOnly: boolean;
	isForRelay?: boolean;
}

export type RelayWrappedFetchOptions = {
	shouldConsiderGetMethod?: boolean;
	operationKind?: 'query' | 'mutation' | 'subscription';
};

export type WrappedFetchRequestInit = RequestInit & {
	confluenceRelayFetchOptions?: RelayWrappedFetchOptions;
};

const appendVariablesToUrl = (url: string, variables: { [key: string]: any }) =>
	`${url}${url.includes('?') ? '&' : '?'}variables=${encodeURIComponent(
		JSON.stringify(variables),
	)}`;

const appendBatchPath = (url: string) => (url.endsWith('/batch') ? url : `${url}/batch`);
const sanitizeMarkingName = (name?: string) => name?.replace(/[^\-.\w]/g, '_');

export const createWrappedFetch = ({
	isForAggOnly,
	isForRelay = false,
}: CreateWrappedFetchOptions) => {
	return (uri: string, options: WrappedFetchRequestInit): Promise<Response> => {
		const payload: Array<{
			operationName: string;
			query: string;
			variables: { [key: string]: any };
		}> = [].concat(typeof options.body === 'string' ? JSON.parse(options.body) : []); // concat normalizes into an array

		const operationNames = payload.map(({ operationName, variables }) =>
			getQueryName(operationName, variables),
		);
		const queriesExcludedFromAggMigration =
			StatsigConfigurations.getStringList('cc-agg-migration-excluded-queries', []) || [];

		const isExcludedFromAggMigration = operationNames.some((name) =>
			queriesExcludedFromAggMigration.includes(name),
		);
		const tenantHostname =
			typeof window !== 'undefined' ? window.location?.hostname : getSSRRenderInputs().host;

		const shouldUseAggEndpoint =
			fg('cc-agg-migration-use-agg-endpoint') ||
			operationNames.includes(SITE_INFORMATION_QUERY_OPERATION_NAME);
		const isUsingAggImplicitly =
			!isExcludedFromAggMigration &&
			!isExcludedFromAggMigration &&
			(shouldUseAggEndpoint || isGCPtenant(tenantHostname));

		// This is a temporary list to force isForAggOnly to true for Forge's extension list queries
		// while Confluence is mid-migration
		const forgeOperationNames = [
			'usePrefilteredExtensionList_forge_ui_Query',
			'getForgeUIExtensionsAsync_prefetchedQuery',
			'getForgeUIExtensionsAsync_Query',
			'useExtensionList_forge_ui_Query',
			'usePrefilteredExtensionList_forge_ui_Query',
			'queryTypedChildrenContentQuery',
		];
		const isForgeExtensionListQuery = operationNames.some((name) =>
			forgeOperationNames.includes(name),
		);

		if (!isForAggOnly && !isForgeExtensionListQuery) {
			if (isUsingAggImplicitly) {
				uri = AGG_SLOW_URI;
			} else {
				// On client side the uri is /cgraphql
				// However when running on the server side we want to dynamic change the URI
				// So we can route the request to the graphql deployment that closer to the data.
				// AGG host is redirected by Tesseract so we don't need to change the URI
				uri = window.GLOBAL_APOLLO_CLIENT_URI || uri;
			}
		}

		// Track individual operations for frontend
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR && typeof window !== 'undefined') {
			networkTracker.trackGraphQLOperations(operationNames);
		}

		// pass as headers for rate-limiting compatibility
		const operationNamesList = operationNames.join(',');
		if (!options.headers) {
			options.headers = {};
		}

		// AGG batch queries require /batch to be added to the uri and an additional header
		if (uri.includes('gateway')) {
			appendHeader(options.headers, 'X-ExperimentalApi', 'confluence-agg-beta');
			if (payload.length === 1) {
				options.body = JSON.stringify(payload[0]);
			} else if (payload.length > 1) {
				appendHeader(options.headers, 'X-ExperimentalApi', 'BatchOperations');
				uri = appendBatchPath(uri);
			}
		}

		appendHeader(options.headers, 'X-APOLLO-OPERATION-NAME', operationNamesList);

		const shouldApplyAggHeaders = shouldUseAggEndpoint ? !isExcludedFromAggMigration : isForAggOnly;

		if (shouldApplyAggHeaders) {
			const header = getAtlAttributionHeader();
			appendHeader(options.headers, 'atl-attribution', header['atl-attribution']);
		}

		if (shouldApplyAggHeaders || process.env.BIFROST_TEMPLATE_BUILD === 'true') {
			// These headers are explicitly forwarded to cc-graphql in cc-frontend-ssr, but we need to apply them here for Bifrost requests to cc-graphql.
			// We forward the same headers for AGG and cc-graphql requests
			const { aggHeaders } = getSSRRenderInputs();
			if (aggHeaders) {
				Object.entries(aggHeaders).forEach(([header, val]) => {
					if (val) {
						appendHeader(options.headers!, header, val);
					}
				});
			}
		}

		// Note: this may overrwrite a header value from above in SSR context, but this ensures
		// it's still applied in SPA context
		if (window['GLOBAL_AGG_REGION_STEERING_HINT']) {
			appendHeader(
				options.headers,
				'Atl-Edge-Region-Hint',
				window['GLOBAL_AGG_REGION_STEERING_HINT']!,
			);
		}

		options.referrerPolicy = options.referrerPolicy || 'same-origin';

		// Tesseract instruments the outgoing http requests automatically, no need to pass tracing headers when running in SSR.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// add experience tracing headers

			const tracingHeaders = getActiveTraceHttpRequestHeaders('' /* url, not used */);
			options.headers = Object.assign(options.headers, tracingHeaders);

			if (expVal('cc_use_browser_tab_id', 'isEnabled', false)) {
				appendHeader(options.headers, 'X-ATL-Confluence-Origin-Tab', getBrowserTabId());
			}
		}

		const relayFetchOptions = options.confluenceRelayFetchOptions;
		const shouldConsiderRelayGetMethod =
			isForRelay &&
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			!process.env.REACT_SSR &&
			relayFetchOptions?.shouldConsiderGetMethod === true &&
			relayFetchOptions.operationKind === 'query' &&
			payload.length === 1 &&
			uri.includes('/pq/') &&
			!fg('confluence_relay_pq_get_killswitch');
		let shouldUseGet = false;
		let fallbackReason: string | undefined;

		if (shouldConsiderRelayGetMethod) {
			const { isGetRequest, reason } = shouldUseGetMethod({
				url: uri,
				variables: payload[0].variables ?? {},
				headers: options.headers,
				payload,
			});
			shouldUseGet = isGetRequest;
			fallbackReason = reason;
		}

		// At this point the query has lost the structure and formatted back to string
		const executeFetch = () => {
			const requestAgg = isForAggOnly || isUsingAggImplicitly || isForgeExtensionListQuery;
			const isRelayPersistedQuery = isForRelay && uri.includes('/gateway/api/graphql/pq/');
			const finalOptions: WrappedFetchRequestInit = { ...options };
			delete finalOptions.confluenceRelayFetchOptions;
			let finalUri = uri;

			if (shouldUseGet) {
				finalOptions.method = 'GET';
				finalUri = appendVariablesToUrl(finalUri, payload[0].variables ?? {});
				delete finalOptions.body;
			} else if (fallbackReason) {
				appendHeader(
					finalOptions.headers as Record<string, string>,
					'X-Request-Fallback-To-Post-Reason',
					fallbackReason,
				);
			}

			if (process.env.BIFROST_TEMPLATE_BUILD === 'true') {
				/**
				 * Request route through AP when x-ssr-asap-audience is provided.
				 */
				const headers = (finalOptions.headers || {}) as Record<string, string>;
				const { isBifrostIcRequest, monolithAwsRegion } = getSSRRenderInputs();

				if (process.env.PILLAR_LOCAL_DEVELOPMENT !== 'true') {
					if (!requestAgg) {
						// We don't set the header for agg because it is handled specially
						appendHeader(headers, 'x-ssr-asap-audience', CC_GRAPHQL_SERVICE_NAME);
						appendHeader(headers, 'x-ssr-service-name', CC_GRAPHQL_SERVICE_NAME);
						if (
							isBifrostIcRequest === true &&
							expVal('confluence_ssr_bifrost_ic', 'isEnabled', false)
						) {
							appendHeader(headers, 'x-archetype-name', CC_GRAPHQL_ARCHETYPE_NAME);
						}
						if (monolithAwsRegion) {
							appendHeader(headers, 'x-ssr-service-region', monolithAwsRegion);
						}
					}
				}
				if (!requestAgg) {
					const { embeddablePageEntityIds } = getSSRRenderInputs();
					if (embeddablePageEntityIds) {
						appendHeader(headers, 'x-atl-embeddablePage-entityIds', embeddablePageEntityIds);
					}
				}
				let baseUrl = '/cgraphql';
				if (requestAgg) {
					baseUrl = isRelayPersistedQuery ? finalUri : AGG_SLOW_URI;
				}
				if (baseUrl.includes('gateway') && payload.length > 1) {
					baseUrl = appendBatchPath(baseUrl);
				}

				const urlWithQuery = isForRelay ? baseUrl : `${baseUrl}?q=${operationNamesList}`;

				// Track SSR measures for Bifrost - fetchBase bypasses cfetch where
				// fetchCountIncr is normally called
				SSRMeasures.fetchCountIncr(1, { isUsingRelay: isForRelay });

				if (requestAgg && !headers['Atl-Edge-Region-Hint']) {
					// eslint-disable-next-line no-console
					console.warn(
						`Gateway GraphQL request missing Atl-Edge-Region-Hint header: ${urlWithQuery}`,
					);
				}

				let queryBoundary: string | undefined | null;
				let guardName: string | undefined;
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					// Which query boundary does this fetch belong to?
					queryBoundary = getQueryBoundary();

					// Which slow query guard does this fetch belong to?
					const storage = getAsyncLocalStorage(SLOW_QUERY_GUARD_STORAGE_NAME);
					guardName = storage?.getStore() as string | undefined;
				}

				// Record this request as in flight so that, if it never comes back, the data
				// fetcher timeout can still report it as the cause.
				const untrackInflightRequest = trackInflightRequest(queryBoundary, {
					method: finalOptions?.method || 'GET',
					url: urlWithQuery,
				});

				return fetchBase({
					name: requestAgg ? 'fetchAgg' : 'fetchCGraphql',
					url: urlWithQuery,
					headers,
					options: finalOptions,
				})
					.finally(untrackInflightRequest)
					.then((result) => {
						if (guardName && window.__TIMED_OUT_SLOW_QUERY_GUARDS__?.has(guardName)) {
							// eslint-disable-next-line no-console
							console.info(
								`Returning data for timed out slowQueryGuard "${guardName}": ${
									finalOptions?.method || 'GET'
								} ${urlWithQuery}`,
							);
						}
						return result;
					});
			} else {
				// Non-bifrost server and client-side fetch
				const urlWithQuery = isForRelay ? finalUri : `${finalUri}?q=${operationNamesList}`;

				const headers = (finalOptions.headers || {}) as Record<string, string>;
				if (requestAgg && !headers['Atl-Edge-Region-Hint']) {
					// eslint-disable-next-line no-console
					console.warn(
						`Gateway GraphQL request missing Atl-Edge-Region-Hint header: ${urlWithQuery}`,
					);
				}

				return (
					// item might not always have a query property - as in the case of persisted queries
					(isExperienceAwareFetchAlwaysEnabled() ? experienceAwareFetch : cfetch)(
						urlWithQuery,
						finalOptions,
					)
				);
			}
		};

		let markingPrefix = 'wf/client-graphql/';
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			const queryBoundary = getQueryBoundary();
			const safeQueryBoundary = queryBoundary?.replace(/[^\-.\w]/g, '_');
			markingPrefix = safeQueryBoundary
				? `ssr-app/prepare/preloadQuery/dataFetcher:${safeQueryBoundary}/fetch:`
				: 'ssr-app/prepare/preloadQuery/fetch:';
		}
		const markingNames = operationNames.map((name) => {
			// Note: whole batch goes to cc-graphql, if one query is excluded w/in a batch
			if (isForRelay) {
				name = `relay_${name}`;
			} else if (shouldApplyAggHeaders) {
				name = `agg_${name}`;
			}
			return `${markingPrefix}${sanitizeMarkingName(name)}`;
		});
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			// Track individual GraphQL operations count for SSR
			SSRMeasures.operationsCountIncr(operationNames.length);

			// Wrap executeFetch to track GraphQL errors
			const errorTrackingExecuteFetch = async () => {
				try {
					return await executeFetch();
				} catch (error) {
					// Track GraphQL errors during SSR
					SSRMeasures.errorCountIncr(operationNames.length);
					throw error;
				}
			};

			return SSRMeasures.run(markingNames, errorTrackingExecuteFetch);
		}
		// Ignore batch queries because they are not deterministic. Don't make sense to show on performance waterfall.
		return operationNames.length > 1
			? executeFetch()
			: WaterfallMeasures.run(markingNames, executeFetch);
	};
};
